import { useEffect, useSyncExternalStore } from 'react';

import { storage } from '../utils/local-storage';

export type ThemeMode = 'system' | 'light' | 'dark';
export type ResolvedThemeMode = 'light' | 'dark';

const prefersDark = () => window.matchMedia('(prefers-color-scheme: dark)');

const subscribe = (onChange: () => void) => {
  const media = prefersDark();
  window.addEventListener('k3x-theme-mode-change', onChange);
  window.addEventListener('storage', onChange);
  media.addEventListener('change', onChange);
  return () => {
    window.removeEventListener('k3x-theme-mode-change', onChange);
    window.removeEventListener('storage', onChange);
    media.removeEventListener('change', onChange);
  };
};

const getSnapshot = () => {
  const stored = storage.get('k3x_theme_mode');
  if (stored === 'light' || stored === 'dark') {
    return stored;
  }
  return prefersDark().matches ? 'system:dark' : 'system:light';
};

export const useThemeMode = () => {
  const snapshot = useSyncExternalStore(subscribe, getSnapshot, () => 'system:light');
  const mode: ResolvedThemeMode = snapshot === 'dark' || snapshot === 'system:dark' ? 'dark' : 'light';
  const themeMode: ThemeMode = (snapshot.startsWith('system') ? 'system' : snapshot) as ThemeMode;

  useEffect(() => {
    document.documentElement.style.colorScheme = mode;
  }, [mode]);

  const setThemeMode = (next: ThemeMode) => {
    storage.set('k3x_theme_mode', next);
    window.dispatchEvent(new Event('k3x-theme-mode-change'));
  };

  return {
    mode,
    themeMode,
    resolvedMode: mode,
    setThemeMode,
  };
};
